import * as THREE from 'three';

/**
 * Spatial predicates that resolve to a concrete set of geometric corners.
 *
 * This is the addressing layer `moveCorners` and `relax` are built on. It
 * exists so a caller can say "everything above the eyeline on the left" once
 * instead of hunting ids out of a full adjacency dump before every edit.
 *
 * COORDINATE SPACES. Every distance-based selector takes an explicit
 * `space: "local" | "world"`, defaulting to `"local"`.
 *
 *   local  mesh-local === object-local. Topology positions are stored in the
 *          object's own frame and `buildGeometryFromTopology` emits geometry in
 *          that frame, so there is no third space to distinguish. This is also
 *          the space a relative `delta` is expressed in by default, so a
 *          selector and a displacement share units.
 *   world  the same positions through the object's world matrix.
 *
 * `screenRect` has no `space` option: a pixel is inherently the result of a
 * world-space position projected through a camera, so it always uses world
 * positions regardless of what its siblings are set to.
 *
 * BOUNDS ARE INCLUSIVE throughout, matching the existing `idsInsideScreenRect`
 * box-select in src/selection.js — a corner exactly on a box face, exactly at a
 * sphere's radius, or exactly on a rectangle edge is selected. `halfSpace` is
 * the exception only because it takes its comparison operator explicitly.
 */

const SELECTOR_KEYS = new Set(['box', 'halfSpace', 'sphere', 'screenRect', 'expand', 'all', 'any', 'not']);
const SPACES = new Set(['local', 'world']);
const HALF_SPACE_OPS = {
  '<': (value, threshold) => value < threshold,
  '<=': (value, threshold) => value <= threshold,
  '>': (value, threshold) => value > threshold,
  '>=': (value, threshold) => value >= threshold,
};
const AXIS_INDEX = { x: 0, y: 1, z: 2 };

function requireVec3(value, label) {
  if (!Array.isArray(value) || value.length !== 3 || !value.every((n) => typeof n === 'number' && Number.isFinite(n))) {
    throw new Error(`${label} must be a complete [x, y, z] of finite numbers`);
  }
  return value;
}

function requireSpace(raw, label) {
  const space = raw ?? 'local';
  if (!SPACES.has(space)) throw new Error(`${label}.space must be "local" or "world", got ${JSON.stringify(raw)}`);
  return space;
}

/**
 * Corner positions in the requested space.
 *
 * Cached per space per resolve, because a nested selector tree can ask for the
 * same space many times and a world transform of every corner is not free.
 */
function positionsIn(context, space) {
  const cached = context._positions.get(space);
  if (cached) return cached;
  let positions;
  if (space === 'local') {
    positions = new Map(context.corners.map((corner) => [corner.id, corner.position]));
  } else {
    const point = new THREE.Vector3();
    positions = new Map(context.corners.map((corner) => [
      corner.id,
      point.fromArray(corner.position).applyMatrix4(context.matrixWorld).toArray(),
    ]));
  }
  context._positions.set(space, positions);
  return positions;
}

function allCornerIds(context) {
  return context.corners.map((corner) => corner.id);
}

function selectWhere(context, space, predicate) {
  const positions = positionsIn(context, space);
  const selected = new Set();
  for (const [cornerId, position] of positions) {
    if (predicate(position)) selected.add(cornerId);
  }
  return selected;
}

function evaluateBox(spec, context) {
  const space = requireSpace(spec.space, 'box');
  const min = requireVec3(spec.min, 'box.min');
  const max = requireVec3(spec.max, 'box.max');
  // Normalised, so a box given "inside out" behaves the same as one given the
  // right way round — the same courtesy idsInsideScreenRect extends to a
  // backwards drag rectangle.
  const lo = min.map((v, i) => Math.min(v, max[i]));
  const hi = min.map((v, i) => Math.max(v, max[i]));
  return selectWhere(context, space, (p) => p.every((v, i) => v >= lo[i] && v <= hi[i]));
}

function evaluateHalfSpace(spec, context) {
  const space = requireSpace(spec.space, 'halfSpace');
  const axis = AXIS_INDEX[spec.axis];
  if (axis === undefined) throw new Error(`halfSpace.axis must be "x", "y" or "z", got ${JSON.stringify(spec.axis)}`);
  const compare = HALF_SPACE_OPS[spec.op];
  if (!compare) {
    throw new Error(`halfSpace.op must be one of ${Object.keys(HALF_SPACE_OPS).join(', ')}, got ${JSON.stringify(spec.op)}`);
  }
  if (typeof spec.value !== 'number' || !Number.isFinite(spec.value)) {
    throw new Error('halfSpace.value must be a finite number');
  }
  return selectWhere(context, space, (p) => compare(p[axis], spec.value));
}

function evaluateSphere(spec, context) {
  const space = requireSpace(spec.space, 'sphere');
  const center = requireVec3(spec.center, 'sphere.center');
  if (typeof spec.radius !== 'number' || !Number.isFinite(spec.radius) || spec.radius < 0) {
    throw new Error('sphere.radius must be a finite number >= 0');
  }
  const radiusSquared = spec.radius ** 2;
  return selectWhere(context, space, (p) => {
    const dx = p[0] - center[0];
    const dy = p[1] - center[1];
    const dz = p[2] - center[2];
    return dx * dx + dy * dy + dz * dz <= radiusSquared;
  });
}

/**
 * Corners whose world position projects inside a pixel rectangle of the given
 * view spec's raster.
 *
 * Convention matches `idsInsideScreenRect` exactly — origin top-left, y down,
 * inclusive bounds, corners outside the frustum depth range discarded — with
 * one deliberate difference: coordinates are relative to the view spec's own
 * `width`/`height` raster, with (0,0) at its top-left. The existing helper adds
 * a DOM canvas offset because it handles pointer events; there is no DOM here.
 *
 * This selects by projected position only and does NOT test occlusion: a corner
 * on the far side of the mesh that falls inside the rectangle is selected.
 * Documented rather than fixed — occlusion-aware selection is out of Phase 1.
 */
function evaluateScreenRect(spec, context) {
  if (!context.resolveScreenSpec) {
    throw new Error('screenRect requires a resolveScreenSpec in the resolve context');
  }
  for (const key of ['left', 'top', 'right', 'bottom']) {
    if (typeof spec[key] !== 'number' || !Number.isFinite(spec[key])) {
      throw new Error(`screenRect.${key} must be a finite number`);
    }
  }
  const { camera, width, height } = context.resolveScreenSpec(spec.viewSpec);
  const left = Math.min(spec.left, spec.right);
  const right = Math.max(spec.left, spec.right);
  const top = Math.min(spec.top, spec.bottom);
  const bottom = Math.max(spec.top, spec.bottom);

  const positions = positionsIn(context, 'world');
  const point = new THREE.Vector3();
  const selected = new Set();
  for (const [cornerId, position] of positions) {
    point.fromArray(position).project(camera);
    if (!Number.isFinite(point.z) || point.z < -1 || point.z > 1) continue;
    const x = ((point.x + 1) / 2) * width;
    const y = ((1 - point.y) / 2) * height;
    if (x >= left && x <= right && y >= top && y <= bottom) selected.add(cornerId);
  }
  return selected;
}

/**
 * Ring expansion over the SCULPT-neighbour graph — every raw face edge,
 * coplanar diagonals included — never `corners[].edgeIds`, which omits those
 * diagonals and would make expansion leak around the rim of a flat face
 * instead of crossing it.
 */
function evaluateExpand(spec, context) {
  if (!Array.isArray(spec.cornerIds) || spec.cornerIds.some((id) => typeof id !== 'string')) {
    throw new Error('expand.cornerIds must be an array of corner id strings');
  }
  if (!Number.isInteger(spec.rings) || spec.rings < 0) {
    throw new Error('expand.rings must be an integer >= 0');
  }
  const known = new Set(allCornerIds(context));
  for (const id of spec.cornerIds) {
    if (!known.has(id)) throw new Error(`expand.cornerIds contains unknown corner id '${id}'`);
  }

  let frontier = new Set(spec.cornerIds);
  const reached = new Set(frontier);
  for (let ring = 0; ring < spec.rings; ring++) {
    const next = new Set();
    for (const cornerId of frontier) {
      for (const neighbour of context.graph.nodes.get(cornerId) ?? []) {
        if (!reached.has(neighbour)) {
          reached.add(neighbour);
          next.add(neighbour);
        }
      }
    }
    if (next.size === 0) break;
    frontier = next;
  }
  return reached;
}

function evaluateAll(specs, context) {
  if (!Array.isArray(specs) || specs.length === 0) throw new Error('all must be a non-empty array of selectors');
  let result = null;
  for (const spec of specs) {
    const next = evaluateSelector(spec, context);
    if (result === null) result = next;
    else for (const id of [...result]) if (!next.has(id)) result.delete(id);
  }
  return result;
}

function evaluateAny(specs, context) {
  if (!Array.isArray(specs) || specs.length === 0) throw new Error('any must be a non-empty array of selectors');
  const result = new Set();
  for (const spec of specs) {
    for (const id of evaluateSelector(spec, context)) result.add(id);
  }
  return result;
}

function evaluateNot(spec, context) {
  const excluded = evaluateSelector(spec, context);
  return new Set(allCornerIds(context).filter((id) => !excluded.has(id)));
}

function evaluateSelector(selector, context) {
  if (!selector || typeof selector !== 'object' || Array.isArray(selector)) {
    throw new Error('a selector must be an object');
  }
  const keys = Object.keys(selector);
  if (keys.length !== 1) {
    throw new Error(`a selector must have exactly one key (${[...SELECTOR_KEYS].join(', ')}), got ${JSON.stringify(keys)}`);
  }
  const [key] = keys;
  if (!SELECTOR_KEYS.has(key)) {
    throw new Error(`unknown selector '${key}'; expected one of ${[...SELECTOR_KEYS].join(', ')}`);
  }
  const spec = selector[key];
  switch (key) {
    case 'box': return evaluateBox(spec, context);
    case 'halfSpace': return evaluateHalfSpace(spec, context);
    case 'sphere': return evaluateSphere(spec, context);
    case 'screenRect': return evaluateScreenRect(spec, context);
    case 'expand': return evaluateExpand(spec, context);
    case 'all': return evaluateAll(spec, context);
    case 'any': return evaluateAny(spec, context);
    default: return evaluateNot(spec, context);
  }
}

/**
 * Resolve a selector tree to sorted corner ids.
 *
 * `context` is `{ corners, graph, matrixWorld, resolveScreenSpec }`:
 *   corners           computeGeometricAdjacency(topology).corners
 *   graph             buildSculptGraph(...) — only needed for `expand`
 *   matrixWorld       THREE.Matrix4 — only needed for space:"world"/screenRect
 *   resolveScreenSpec (viewSpec) => { camera, width, height } — screenRect only
 *
 * Passed in rather than derived here so this module stays a pure predicate
 * evaluator with no registry, no adjacency recomputation, and no second way to
 * fail on a degenerate mesh.
 */
export function resolveSelector(selector, { corners, graph = null, matrixWorld = null, resolveScreenSpec = null }) {
  if (!Array.isArray(corners)) throw new Error('resolveSelector requires the corners array');
  const context = {
    corners,
    graph,
    matrixWorld: matrixWorld ?? new THREE.Matrix4(),
    resolveScreenSpec,
    _positions: new Map(),
  };
  return [...evaluateSelector(selector, context)].sort();
}
